import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import '../../assets/css/ChatBot.css';
import '../../assets/css/ChatBotMobile.css';
import '../../assets/css/FileAttachments.css';
// import '../../assets/css/markdown-chat.css';

// Import custom hooks and services
import useChatBot from '../../hooks/useChatBot';
// We now import useChatConsent directly from the consent-manager package
import { useChatConsent } from '../../consent-manager';

// Constants
const WELCOME_MESSAGE = {
  sender: 'bot',
  content: `Hi! I'm Samyama—your AI assistant. I can help you explore our platform, answer questions, or connect you to the right tools.\n\nWhat would you like to know?`,
  timestamp: new Date().toISOString()
};

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [localMessages, setLocalMessages] = useState([]);
  
  // Use our custom hooks for cookie consent and chat functionality
  const { 
    hasConsent: functionalCookiesAccepted, // We use functional cookies category for chat
    consentLoaded
  } = useChatConsent();
  
  const { 
    messages, 
    loading: isLoading, 
    sendMessage: sendChatMessage, 
    loadConversationHistory,
    error
  } = useChatBot(); // No need to pass consent state, hook manages it internally
  
  // Monitor consent state changes
  // useEffect(() => {
  //   // Log consent state changes to help with debugging
  //   console.log('ChatBot: Consent state -', 
  //     functionalCookiesAccepted ? 'Functional cookies accepted' : 'Functional cookies not accepted');
  // }, [functionalCookiesAccepted]);
  
  // Track if we've already initialized to prevent duplicate calls
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Handle changes in cookie consent status
  useEffect(() => {
    // Only process consent changes when Usercentrics has actually loaded
    if (!consentLoaded) {
      console.log('ChatBot: Waiting for consent to be fully loaded...');
      return;
    }

    console.log('ChatBot: Functional cookie consent state changed:', 
      functionalCookiesAccepted ? 'accepted' : 'rejected');
    
    // Reset state when consent changes to ensure a clean slate
    if (hasInitialized) {
      // console.log('ChatBot: Resetting chat state due to consent change');
      setLocalMessages([WELCOME_MESSAGE]);
    }
    
    // If functional cookies are accepted, try to load conversation history
    // Note: Custom cookie handling will be implemented in a separate package
    if (functionalCookiesAccepted) {
      // console.log('ChatBot: Functional cookies accepted, loading conversation history');
      loadConversationHistory();
    }
    
    setHasInitialized(true);
  }, [functionalCookiesAccepted, consentLoaded, loadConversationHistory, hasInitialized]);

  
  // Keep local messages state in sync with hook messages
  useEffect(() => {
    if (messages && messages.length > 0) {
      // console.log('ChatBot: Received', messages.length, 'messages from the backend');
      setLocalMessages(messages);
    } else if (messages && messages.length === 0 && localMessages.length > 1) {
      // If we got an empty messages array from the backend but had local messages,
      // it means we've switched to ephemeral mode - reset to welcome message
      // console.log('ChatBot: Switching to ephemeral mode, resetting to welcome');
      setLocalMessages([WELCOME_MESSAGE]);
    }
  }, [messages, localMessages.length]);

  // Reference for the chat thread container
  const chatThreadRef = useRef(null);

  // Check for existing conversation history
  const checkExistingSession = useCallback(() => {
    if (functionalCookiesAccepted) {
      loadConversationHistory();
    }
  }, [functionalCookiesAccepted, loadConversationHistory]);

  // Load conversation when chat opens and functional cookies are accepted
  useEffect(() => {
    if (isOpen && functionalCookiesAccepted && consentLoaded) {
      checkExistingSession();
    }
  }, [isOpen, functionalCookiesAccepted, consentLoaded, checkExistingSession]);
  
  // Auto-scroll to bottom when new message is added or chat is opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(scrollToBottom, 100); // Small delay to ensure rendering is complete
    }
  }, [localMessages, isOpen]);

  // Use welcome message when there are no messages and chat is opened
  useEffect(() => {
    if (isOpen && localMessages.length === 0 && !isLoading) {
      // console.log('Chat opened with no messages, showing welcome message');
      setLocalMessages([WELCOME_MESSAGE]);
    }
  }, [isOpen, localMessages.length, isLoading]);

  // Handle sending messages through backend via the chat hook
  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;
    
    const messageContent = inputValue.trim();
    
    // Clear input field immediately for better UX
    setInputValue('');
    
    // Mark that user has interacted with the chat
    if (!hasInteracted) {
      setHasInteracted(true);
    }
    
    // Create a temporary user message for immediate display
    const tempUserMessage = {
      sender: 'user',
      content: messageContent,
      timestamp: new Date().toISOString()
    };
    
    // Add user message to local UI immediately for responsiveness
    setLocalMessages(prev => [...prev, tempUserMessage]);
    
    // console.log('Sending message with functional cookie consent:', functionalCookiesAccepted ? 'accepted' : 'denied');
    
    // Send message to backend via the useChatBot hook
    // The hook will handle whether to use cookies or ephemeral mode based on consent
    await sendChatMessage(messageContent);
    
    // The messages will be updated via the useEffect that watches the messages from the hook
    // No need to manually add the bot response here
    
    // Scroll to the bottom of the chat
    setTimeout(scrollToBottom, 100);
  };

  const toggleChatBot = () => {
    const newOpenState = !isOpen;
    setIsOpen(newOpenState);
    
    if (!hasInteracted && newOpenState) {
      setHasInteracted(true);
    }
    
    // If opening the chat, consider loading history
    if (newOpenState) {
      // console.log('Chat opened, functional cookie consent state:', functionalCookiesAccepted ? 'accepted' : 'denied');
      // The useChatBot hook will handle whether to load history or use ephemeral mode
      // based on the current cookie consent state
      loadConversationHistory();
    }
  };
  
  // const handleInputChange = (e) => {
  //   setInputValue(e.target.value);
  // };

  const scrollToBottom = () => {
    if (chatThreadRef.current) {
      chatThreadRef.current.scrollTop = chatThreadRef.current.scrollHeight;
    }
  };

  return (
    <div className="chat-bot-container">
      {/* Chat toggle button */}
      {!isOpen && (
        <div className="chat-bot-button" onClick={toggleChatBot}>
          <img src="/chatwidget/chatbotlogo1.png" alt="Chat" className="chat-logo" />
        </div>
      )}
      
      {/* Chat window */}
      {isOpen && (
        <div className="chat-bot-window">
          {/* Header */}
          <div className="chat-bot-header">
            <div className="chat-bot-title">
              <img src="/chatwidget/logo.png" alt="Samyama" className="chat-header-logo" />
              <div className="chat-title-text">
                <span className="chat-main-title">Samyama</span>
                <div className="chat-subtitle">
                  <span>Your mindful companion</span>
                  <span className="tagline">Live. Aware. Ready. Always</span>
                </div>
              </div>
            </div>
            <div className="chat-bot-controls">
              {/* <img 
                src="/chatwidget/Vector.png" 
                alt="Close chat" 
                className="chat-control-icon vector-icon" 
                onClick={toggleChatBot} 
                style={{ cursor: 'pointer' }}
              />  */}
              {/* <img 
                src="/chatwidget/Group 4.png" 
                alt="" 
                className="chat-control-icon group-icon" 
              /> */}
              <img 
                src="/chatwidget/close-icon.png" 
                alt="Close" 
                className="chat-control-icon close-icon" 
                onClick={toggleChatBot} 
              />
            </div>
          </div>
          
          {/* Chat thread */}
          <div className="chat-bot-thread" ref={chatThreadRef}>
            {localMessages.length === 0 && (
              <div className="chat-message bot-message">
                <div className="message-content">
                  {WELCOME_MESSAGE.content}
                </div>
              </div>
            )}
            {localMessages.map((message, index) => (
                <div key={index} className={`chat-message ${message.sender === 'visitor' ? 'visitor-message' : 'bot-message'}`}>
                  <div className="message-content markdown-content">
                    <ReactMarkdown 
                      components={{
                        // Ensure proper spacing for list items
                        li: ({node, ...props}) => <li style={{marginBottom: '0.5em'}} {...props} />,
                        // Ensure proper spacing for paragraphs within list items
                        p: ({node, ...props}) => <p style={{margin: '0 0 0.5em 0'}} {...props} />,
                        // Add proper styling for ordered lists
                        ol: ({node, ...props}) => <ol style={{margin: '0.5em 0', paddingLeft: '1.5em'}} {...props} />,
                        // Add proper styling for unordered lists
                        ul: ({node, ...props}) => <ul style={{margin: '0.5em 0', paddingLeft: '1.5em'}} {...props} />,
                        // Handle links to open in new tab and prevent chat closure
                        a: ({node, ...props}) => (
                          <a 
                            {...props} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(props.href, '_blank');
                              e.preventDefault();
                            }}
                          >
                            {props.children || 'Link'}
                          </a>
                        ),
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </div>
                </div>
            ))}
            {isLoading && (
              <div className="chat-message bot-message typing">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            {error && (
              <div className="chat-message bot-message error-message">
                <div className="message-content">
                  Our chat service is temporarily unavailable. Please try again in a few moments.
                </div>
              </div>
            )}
          </div>
          
          {/* Input area */}
          <div className="chat-bot-input-area">
            <form 
              onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}
              className="chat-form"
              style={{ display: 'flex', width: '100%', gap: '8px' }}
            >  
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder="Type here.."
                className="chat-input"
                disabled={false}
                rows={1}
                style={{ flex: 1, resize: 'none' }}
              />
              <button 
                type="submit"
                className={`send-button ${isLoading ? 'sending' : ''}`}
                // Only disable if there's no input or if we're already sending
                disabled={!inputValue.trim() || isLoading}
                title={isLoading ? 'Sending...' : 'Send message'}
              >
                SEND
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBot;
